// frontend/src/pages/AdminDashboard.jsx
import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext.jsx';
import ContentBlockEditor from '../components/ContentBlockEditor.jsx';
import ContentPromptEditor from '../components/ContentPromptEditor';
import UserManagementView from '../components/UserManagementView.jsx'; // Import the new component
import { PencilIcon, TrashIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon, UsersIcon, BookOpenIcon } from '@heroicons/react/24/solid';

function AdminDashboard() {
  const { token } = useContext(AuthContext);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [editingChapterTitle, setEditingChapterTitle] = useState(null);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [error, setError] = useState('');
  const [currentView, setCurrentView] = useState('chapters'); // State for current view

  useEffect(() => {
    if (currentView === 'chapters') {
      fetchChapters();
    }
    // Add logic here to fetch users if currentView === 'users'
    // This will likely be handled within UserManagementView itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]); // Refetch chapters if view changes back to chapters

  const fetchChapters = async () => {
    // ... existing fetchChapters code ...
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch chapters.');
      }

      const data = await response.json();
      data.sort((a, b) => a.title.localeCompare(b.title));
      setChapters(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while fetching chapters.');
    }
  };

  // --- All other existing functions (createChapter, updateChapter, etc.) remain the same ---
  const createChapter = async () => {
    const title = prompt('Enter the title of the new chapter:');
    if (!title) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title, contentBlocks: [], feedbackPrompt: '' }), // Add default feedbackPrompt
      });

      if (!response.ok) {
        throw new Error('Failed to create chapter.');
      }

      const newChapter = await response.json();
      // Add and re-sort
      const updatedChapters = [...chapters, newChapter].sort((a, b) => a.title.localeCompare(b.title));
      setChapters(updatedChapters);
      setSelectedChapter(newChapter); // Select the newly created chapter
      setSelectedBlock(null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while creating the chapter.');
    }
  };

  const updateChapter = async (chapterId, updatedData) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters/${chapterId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        throw new Error('Failed to update chapter.');
      }

      const updatedChapter = await response.json();
      // Update and re-sort
      const updatedChapters = chapters.map(ch => (ch._id === chapterId ? updatedChapter : ch)).sort((a, b) => a.title.localeCompare(b.title));
      setChapters(updatedChapters);

      // If the currently selected chapter was updated, update the state
      if (selectedChapter && selectedChapter._id === chapterId) {
        setSelectedChapter(updatedChapter);
      }
      setEditingChapterTitle(null); // Exit editing mode
      setNewChapterTitle(''); // Clear input
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while updating the chapter.');
    }
  };

  const handleStartEditChapterTitle = (chapter) => {
    setEditingChapterTitle(chapter._id);
    setNewChapterTitle(chapter.title);
  };

  const handleCancelEditChapterTitle = () => {
    setEditingChapterTitle(null);
    setNewChapterTitle('');
  };

  const handleSaveChapterTitle = (chapterId) => {
    if (!newChapterTitle.trim()) {
      setError('Chapter title cannot be empty.');
      return;
    }
    updateChapter(chapterId, { title: newChapterTitle });
  };

  const handleFeedbackPromptUpdate = async (newPrompt) => {
    if (!selectedChapter) return;
    try {
      // Use the generic updateChapter function
      await updateChapter(selectedChapter._id, { feedbackPrompt: newPrompt });
      // No need to manually update state here, updateChapter handles it
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while updating the feedback prompt.');
    }
  };

  const deleteChapter = async (chapterId, event) => {
    event.stopPropagation(); // Prevent chapter selection when clicking delete
    if (!window.confirm('Are you sure you want to delete this chapter and all its content? This cannot be undone.')) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters/${chapterId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete chapter.');
      }

      setChapters(chapters.filter(ch => ch._id !== chapterId));
      if (selectedChapter && selectedChapter._id === chapterId) {
        setSelectedChapter(null);
        setSelectedBlock(null);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while deleting the chapter.');
    }
  };

  const selectChapter = (chapter) => {
    if (editingChapterTitle === chapter._id) return; // Don't select if editing title
    setSelectedChapter(chapter);
    setSelectedBlock(null); // Deselect block when changing chapter
  };

  const selectBlock = (block) => {
    setSelectedBlock(block);
  };

  const moveBlock = async (fromIndex, toIndex) => {
    if (!selectedChapter || fromIndex < 0 || toIndex < 0 || fromIndex >= selectedChapter.contentBlocks.length || toIndex >= selectedChapter.contentBlocks.length) {
      return;
    }

    const updatedBlocks = [...selectedChapter.contentBlocks];
    const [blockToMove] = updatedBlocks.splice(fromIndex, 1);
    updatedBlocks.splice(toIndex, 0, blockToMove);

    // Use the generic updateChapter function
    try {
      await updateChapter(selectedChapter._id, { contentBlocks: updatedBlocks });
      // updateChapter handles updating selectedChapter state if necessary
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while reordering the content blocks.');
    }
  };

  const handleAddBlock = async (blockType = 'text') => {
    if (!selectedChapter) return;

    let newBlockDataPayload;

    switch (blockType) {
        case 'text':
            newBlockDataPayload = { type: blockType, data: '<p>New Text Content</p>' };
            break;
        case 'image':
            newBlockDataPayload = { type: blockType, data: '' }; // URL for image
            break;
        case 'question':
            newBlockDataPayload = { type: blockType, data: { type: 'short_answer', prompt: 'New Question Prompt', options: [] } };
            break;
        case 'table':
            newBlockDataPayload = {
                type: blockType,
                data: {
                    headings: ['Heading 1'],
                    rows: [['']],
                    frozenCells: {},
                    autoFillCells: {},
                    tooltips: {},
                    fixedColumns: {},
                    textContent: '',
                    selectedColumn: null,
                    autoFillPrompt: 'Default prompt: This table is about [topic]. Given the user summary ({{USER_SUMMARY}}) and the current table structure ({{TABLE}}), please fill in the empty cells that are marked for auto-fill.'
                }
            };
            break;
        case 'ai':
            newBlockDataPayload = {
                type: blockType,
                data: {
                    systemPrompt: 'You are a helpful assistant who helps users with creative projects.',
                    welcomeMessage: 'Hello! How can I assist you with this AI chat block today?',
                }
            };
            break;
        default:
            newBlockDataPayload = { type: blockType, data: 'New Content Block' }; // Should ideally be structured
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters/${selectedChapter._id}/contentblocks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newBlockDataPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add content block.');
      }

      const updatedChapter = await response.json();
      // Update and re-sort chapters (though order likely won't change here)
      const updatedChapters = chapters.map(ch => (ch._id === updatedChapter._id ? updatedChapter : ch)).sort((a, b) => a.title.localeCompare(b.title));
      setChapters(updatedChapters);
      setSelectedChapter(updatedChapter); // Update selected chapter with new block list
      // Optionally select the newly added block
      setSelectedBlock(updatedChapter.contentBlocks[updatedChapter.contentBlocks.length - 1]);
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while adding the content block.');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!selectedChapter || !blockId) return;
    if (!window.confirm('Are you sure you want to delete this content block?')) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/chapters/${selectedChapter._id}/contentblocks/${blockId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete content block.');
      }

      const result = await response.json(); // API returns { chapter: updatedChapter }
      // Update and re-sort chapters
      const updatedChapters = chapters.map(ch => (ch._id === result.chapter._id ? result.chapter : ch)).sort((a, b) => a.title.localeCompare(b.title));
      setChapters(updatedChapters);
      setSelectedChapter(result.chapter); // Update selected chapter state
      setSelectedBlock(null); // Deselect the deleted block
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while deleting the content block.');
    }
  };

  // This function updates the block *locally* first for responsiveness,
  // then triggers the API call to persist the change.
  const handleUpdateBlock = (updatedBlockData) => {
    if (!selectedChapter || !selectedBlock) return;

    // Create the locally updated chapter object
    const updatedChapterLocally = {
      ...selectedChapter,
      contentBlocks: selectedChapter.contentBlocks.map(block =>
        block._id === selectedBlock._id ? { ...block, ...updatedBlockData } : block
      ),
    };

    // Update the state immediately for UI responsiveness
    setChapters(chapters.map(ch => (ch._id === updatedChapterLocally._id ? updatedChapterLocally : ch)));
    setSelectedChapter(updatedChapterLocally);
    setSelectedBlock({ ...selectedBlock, ...updatedBlockData }); // Update selected block state as well

    // Now, persist the change to the backend using the generic updateChapter
    // We send the entire updated contentBlocks array
    updateChapter(selectedChapter._id, { contentBlocks: updatedChapterLocally.contentBlocks })
      .catch(err => {
        // Handle potential error during persistence
        setError('Failed to save block changes to server. Please try again.');
        // Optionally revert local state changes here if persistence fails critically
        console.error("Persistence error:", err);
      });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8 font-body">

      {/* View Navigation */}
      <div className="mb-6 flex space-x-2 border-b border-gray-300">
        <button
          onClick={() => setCurrentView('chapters')}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            currentView === 'chapters'
              ? 'bg-white border border-gray-300 border-b-0 text-black'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <BookOpenIcon className="h-5 w-5 mr-2" /> Chapter Editor
        </button>
        <button
          onClick={() => setCurrentView('users')}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            currentView === 'users'
              ? 'bg-white border border-gray-300 border-b-0 text-black'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <UsersIcon className="h-5 w-5 mr-2" /> User Management
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
          <button onClick={() => setError('')} className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <span className="text-red-500 text-xl">×</span>
          </button>
        </div>
      )}

      {/* Conditional View Rendering */}
      {currentView === 'chapters' && (
        <div className="flex flex-col lg:flex-row lg:space-x-6">
          {/* Column 1: Chapters List */}
          <div className="lg:w-1/4 bg-white p-4 rounded-lg shadow-md mb-6 lg:mb-0 h-fit">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
              <h2 className="text-lg font-heading">Chapters</h2>
              <button
                onClick={createChapter}
                title="Add New Chapter"
                className="flex items-center bg-black text-white px-3 py-1 rounded transition text-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" /> Add
              </button>
            </div>
            <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
              {chapters.map((chapter) => (
                <li
                  key={chapter._id}
                  className={`p-2 rounded cursor-pointer group ${
                    selectedChapter && selectedChapter._id === chapter._id && editingChapterTitle !== chapter._id
                      ? 'bg-gray-200'
                      : 'hover:bg-gray-100'
                  } ${editingChapterTitle === chapter._id ? 'bg-yellow-50' : ''}`}
                  onClick={() => selectChapter(chapter)}
                >
                  {editingChapterTitle === chapter._id ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={newChapterTitle}
                        onChange={(e) => setNewChapterTitle(e.target.value)}
                        className="flex-grow border border-yellow-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-yellow-500"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveChapterTitle(chapter._id)}
                      />
                      <button onClick={() => handleSaveChapterTitle(chapter._id)} className="text-green-600 hover:text-green-800 text-xs p-1">Save</button>
                      <button onClick={handleCancelEditChapterTitle} className="text-red-500 hover:text-red-700 text-xs p-1">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-800 truncate pr-2">{chapter.title}</span>
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleStartEditChapterTitle(chapter); }}
                          title="Edit Title"
                          className="p-1 rounded hover:bg-yellow-200 text-yellow-600 hover:text-yellow-800"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => deleteChapter(chapter._id, e)}
                          title="Delete Chapter"
                          className="p-1 rounded hover:bg-red-200 text-red-500 hover:text-red-700"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
               {chapters.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No chapters created yet.</p>}
            </ul>
          </div>

          {/* Column 2: Content Blocks List */}
          <div className="lg:w-1/4 bg-white p-4 rounded-lg shadow-md mb-6 lg:mb-0 h-fit">
            {selectedChapter ? (
              <>
                <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
                  <h2 className="text-lg font-heading">Content Blocks</h2>
                   <button
                      onClick={() => handleAddBlock('text')}
                      title="Add Text Block"
                      className="flex items-center bg-black text-white px-3 py-1 rounded transition text-sm"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" /> Add Block
                    </button>
                </div>
                <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
                  {selectedChapter.contentBlocks.map((block, index) => (
                    <li
                      key={block._id}
                      className={`p-2 rounded cursor-pointer group ${
                        selectedBlock && selectedBlock._id === block._id
                          ? 'bg-gray-200'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => selectBlock(block)}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-800 truncate pr-2">
                          {index + 1}. {block.type.charAt(0).toUpperCase() + block.type.slice(1)}
                        </span>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {index > 0 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); moveBlock(index, index - 1); }}
                              title="Move Up"
                              className="p-1 rounded hover:bg-blue-200 text-blue-600 hover:text-blue-800"
                            >
                              <ArrowUpIcon className="h-4 w-4" />
                            </button>
                          )}
                          {index < selectedChapter.contentBlocks.length - 1 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); moveBlock(index, index + 1); }}
                              title="Move Down"
                              className="p-1 rounded hover:bg-blue-200 text-blue-600 hover:text-blue-800"
                            >
                              <ArrowDownIcon className="h-4 w-4" />
                            </button>
                          )}
                           <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteBlock(block._id); }}
                              title="Delete Block"
                              className="p-1 rounded hover:bg-red-200 text-red-500 hover:text-red-700"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                        </div>
                      </div>
                    </li>
                  ))}
                   {selectedChapter.contentBlocks.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No content blocks added yet.</p>}
                </ul>
              </>
            ) : (
              <div className="text-center py-10">
                  <p className="text-gray-500">Select a chapter to view or add content blocks.</p>
              </div>
            )}
          </div>

          {/* Column 3: Content Block Editor & Feedback Prompt */}
          <div className="lg:w-2/4 flex flex-col space-y-6">
              <div className="bg-white p-4 rounded-lg shadow-md flex-grow">
                <h2 className="text-lg font-heading mb-4 pb-2 border-b border-gray-200">Editor</h2>
                {selectedBlock ? (
                  <ContentBlockEditor
                    key={selectedBlock._id}
                    chapterId={selectedChapter._id}
                    block={selectedBlock}
                    onUpdate={handleUpdateBlock}
                    onDelete={() => handleDeleteBlock(selectedBlock._id)}
                  />
                ) : (
                   <div className="text-center py-10">
                      <p className="text-gray-500">{selectedChapter ? 'Select a content block to edit.' : 'Select a chapter first.'}</p>
                  </div>
                )}
              </div>

              <div className="bg-white p-4 rounded-lg shadow-md h-fit">
                  <h2 className="text-lg font-heading mb-4 pb-2"> Feedback Prompt</h2>
                  {selectedChapter ? (
                  <ContentPromptEditor
                      key={selectedChapter._id}
                      feedbackPrompt={selectedChapter.feedbackPrompt || ''}
                      onUpdate={handleFeedbackPromptUpdate}
                  />
                  ) : (
                   <div className="text-center py-5">
                      <p className="text-gray-500">Select a chapter to edit its feedback prompt.</p>
                   </div>
                  )}
              </div>
          </div>
        </div>
      )}

      {currentView === 'users' && (
        <UserManagementView token={token} setError={setError} />
      )}

    </div>
  );
}

export default AdminDashboard;